import { useState, useEffect, useCallback, useRef } from "react";

const API_BASE = "https://digitalbalm.com";
const INACTIVITY_TIMEOUT = 60000;
const ALL_COINS = ["BTC","ETH","SOL","BNB","XRP","DOGE","ADA","AVAX","LINK","LTC","XLM","SHIB","PEPE"];
const TIMEFRAMES = [{label:"1D",days:1},{label:"7D",days:7},{label:"30D",days:30},{label:"90D",days:90},{label:"6M",days:180},{label:"1Y",days:365},{label:"ALL",days:9999}];
const CHART_TYPES = ["Line","Area","Candle"];

// Auth helpers — JWT stored in sessionStorage
const getToken = () => sessionStorage.getItem("dash_token");
const setToken = (t) => sessionStorage.setItem("dash_token", t);
const clearToken = () => sessionStorage.removeItem("dash_token");
const authFetch = (url, opts={}) => fetch(url, {
  ...opts,
  headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getToken()}`, ...(opts.headers||{}) }
});

const fmt = (n,d=2) => n==null ? "—" : Number(n).toLocaleString("en-US",{minimumFractionDigits:d,maximumFractionDigits:d});
const fmtPct = (n) => n==null ? "—" : `${n>=0?"+":""}${fmt(n)}%`;
const fmtUsd = (n) => n==null ? "—" : `$${fmt(n,2)}`;

// Sparkline
function Sparkline({data,color,width=120,height=40}){
  if(!data||data.length<2)return<svg width={width} height={height}/>;
  const min=Math.min(...data),max=Math.max(...data),range=max-min||1;
  const pts=data.map((v,i)=>{const x=(i/(data.length-1))*width,y=height-((v-min)/range)*(height-4)-2;return`${x},${y}`;}).join(" ");
  return(<svg width={width} height={height} style={{overflow:"visible"}}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/></svg>);
}

// Interactive Chart
function InteractiveChart({history,symbol,chartType,timeframeDays}){
  const svgRef=useRef(null);
  const[tooltip,setTooltip]=useState(null);
  const filtered=history?.filter(d=>{
    if(timeframeDays>=9999)return true;
    return new Date(d.date).getTime()>=Date.now()-timeframeDays*86400000;
  })||[];
  if(filtered.length<2)return<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",color:"rgba(255,255,255,0.3)",fontSize:"13px"}}>No chart data</div>;
  const W=800,H=260,PAD={top:20,right:20,bottom:36,left:72};
  const prices=filtered.map(d=>d.price||d.close||0);
  const minP=Math.min(...prices),maxP=Math.max(...prices),range=maxP-minP||1;
  const cW=W-PAD.left-PAD.right,cH=H-PAD.top-PAD.bottom;
  const xS=i=>PAD.left+(i/(filtered.length-1))*cW;
  const yS=v=>PAD.top+cH-((v-minP)/range)*cH;
  const isUp=prices[prices.length-1]>=prices[0];
  const cc=isUp?"#22c55e":"#ef4444";
  const gridLines=Array.from({length:6},(_,i)=>{const v=minP+(range*i/5);return{y:yS(v),label:`$${v>=1000?(v/1000).toFixed(1)+"k":v.toFixed(v<1?4:2)}`};});
  const xLabels=[];const step=Math.max(1,Math.floor(filtered.length/6));
  for(let i=0;i<filtered.length;i+=step){const d=new Date(filtered[i].date);xLabels.push({x:xS(i),label:d.toLocaleDateString("en-US",{month:"short",day:"numeric"})});}
  const linePath=filtered.map((d,i)=>`${i===0?"M":"L"}${xS(i)},${yS(prices[i])}`).join(" ");
  const areaPath=linePath+` L${xS(filtered.length-1)},${PAD.top+cH} L${xS(0)},${PAD.top+cH} Z`;
  const onMove=e=>{
    const svg=svgRef.current;if(!svg)return;
    const rect=svg.getBoundingClientRect();
    const mx=(e.clientX-rect.left)*(W/rect.width);
    const idx=Math.max(0,Math.min(filtered.length-1,Math.round(((mx-PAD.left)/cW)*(filtered.length-1))));
    setTooltip({x:xS(idx),y:yS(prices[idx]),price:prices[idx],date:filtered[idx].date});
  };
  return(
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"100%",cursor:"crosshair"}} onMouseMove={onMove} onMouseLeave={()=>setTooltip(null)}>
      <defs><linearGradient id={`g${symbol}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={cc} stopOpacity="0.25"/><stop offset="100%" stopColor={cc} stopOpacity="0"/></linearGradient></defs>
      {gridLines.map((g,i)=>(<g key={i}><line x1={PAD.left} x2={W-PAD.right} y1={g.y} y2={g.y} stroke="rgba(255,255,255,0.06)" strokeWidth="1"/><text x={PAD.left-8} y={g.y+4} fill="rgba(255,255,255,0.35)" fontSize="10" textAnchor="end">{g.label}</text></g>))}
      {xLabels.map((xl,i)=>(<text key={i} x={xl.x} y={H-8} fill="rgba(255,255,255,0.35)" fontSize="10" textAnchor="middle">{xl.label}</text>))}
      {chartType==="Area"&&<path d={areaPath} fill={`url(#g${symbol})`}/>}
      {(chartType==="Line"||chartType==="Area")&&<path d={linePath} fill="none" stroke={cc} strokeWidth="2" strokeLinejoin="round"/>}
      {chartType==="Candle"&&filtered.map((d,i)=>{
        const o=d.open||prices[i],c=d.close||prices[i],h=d.high||prices[i],l=d.low||prices[i];
        const x=xS(i),col=c>=o?"#22c55e":"#ef4444",bT=yS(Math.max(o,c)),bB=yS(Math.min(o,c)),bH=Math.max(1,bB-bT),cw=Math.max(1,cW/filtered.length*0.6);
        return(<g key={i}><line x1={x} x2={x} y1={yS(h)} y2={yS(l)} stroke={col} strokeWidth="1"/><rect x={x-cw/2} y={bT} width={cw} height={bH} fill={col}/></g>);
      })}
      {tooltip&&(<g>
        <line x1={tooltip.x} x2={tooltip.x} y1={PAD.top} y2={PAD.top+cH} stroke="rgba(255,255,255,0.25)" strokeWidth="1" strokeDasharray="4,3"/>
        <circle cx={tooltip.x} cy={tooltip.y} r="4" fill={cc} stroke="#1a1a2e" strokeWidth="2"/>
        <rect x={Math.min(tooltip.x+8,W-140)} y={tooltip.y-28} width="132" height="44" rx="6" fill="#1e2035" stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
        <text x={Math.min(tooltip.x+14,W-134)} y={tooltip.y-12} fill="rgba(255,255,255,0.5)" fontSize="10">{new Date(tooltip.date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</text>
        <text x={Math.min(tooltip.x+14,W-134)} y={tooltip.y+6} fill="#fff" fontSize="12" fontWeight="600">${Number(tooltip.price).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</text>
      </g>)}
    </svg>
  );
}

// Timeout modal
function TimeoutModal({secondsLeft,onStay}){
  return(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,backdropFilter:"blur(8px)"}}>
    <div style={{background:"#1a1b2e",border:"1px solid rgba(255,255,255,0.12)",borderRadius:"16px",padding:"40px",textAlign:"center",maxWidth:"340px",width:"90%"}}>
      <div style={{fontSize:"44px",marginBottom:"14px"}}>⏱️</div>
      <div style={{fontSize:"20px",fontWeight:"700",color:"#fff",marginBottom:"8px"}}>Session Expiring</div>
      <div style={{fontSize:"13px",color:"rgba(255,255,255,0.5)",marginBottom:"24px"}}>Logging out in <span style={{color:"#f59e0b",fontWeight:"700",fontSize:"18px"}}>{secondsLeft}s</span></div>
      <button onClick={onStay} style={{background:"linear-gradient(135deg,#6366f1,#8b5cf6)",border:"none",color:"#fff",padding:"12px 28px",borderRadius:"10px",fontSize:"14px",fontWeight:"600",cursor:"pointer",width:"100%"}}>Stay Active</button>
    </div>
  </div>);
}

// Login
function LoginScreen({onLogin}){
  const[pass,setPass]=useState("");
  const[error,setError]=useState("");
  const[loading,setLoading]=useState(false);
  const handle=async()=>{
    setLoading(true);setError("");
    try{
      const r=await fetch(`${API_BASE}/api/login`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({password:pass})});
      if(!r.ok){setError("Incorrect password");setPass("");}
      else{const j=await r.json();setToken(j.token||j.access_token||"ok");onLogin();}
    }catch(e){setError("Connection error");}
    setLoading(false);
  };
  return(<div style={{minHeight:"100vh",background:"#0d0e1a",display:"flex",alignItems:"center",justifyContent:"center"}}>
    <div style={{background:"#1a1b2e",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"20px",padding:"48px 40px",width:"360px",textAlign:"center"}}>
      <div style={{fontSize:"40px",marginBottom:"12px"}}>📈</div>
      <div style={{fontSize:"24px",fontWeight:"800",color:"#fff",letterSpacing:"-0.5px",marginBottom:"4px"}}>DigitalBalm</div>
      <div style={{fontSize:"13px",color:"rgba(255,255,255,0.4)",marginBottom:"32px"}}>Crypto Intelligence Dashboard</div>
      <input type="password" value={pass} placeholder="Enter password"
        onChange={e=>{setPass(e.target.value);setError("");}}
        onKeyDown={e=>e.key==="Enter"&&handle()}
        style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${error?"#ef4444":"rgba(255,255,255,0.1)"}`,borderRadius:"10px",padding:"12px 16px",color:"#fff",fontSize:"14px",boxSizing:"border-box",marginBottom:"8px",outline:"none"}}/>
      {error&&<div style={{color:"#ef4444",fontSize:"12px",marginBottom:"8px"}}>{error}</div>}
      <button onClick={handle} disabled={loading} style={{width:"100%",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",border:"none",color:"#fff",padding:"13px",borderRadius:"10px",fontSize:"15px",fontWeight:"600",cursor:"pointer",marginTop:"8px",opacity:loading?0.7:1}}>
        {loading?"Signing in...":"Sign In"}
      </button>
    </div>
  </div>);
}

export default function App(){
  const[loggedIn,setLoggedIn]=useState(!!getToken());
  const[signals,setSignals]=useState([]);
  const[positions,setPositions]=useState([]);
  const[market,setMarket]=useState({});
  const[projections,setProjections]=useState([]);
  const[selectedCoin,setSelectedCoin]=useState("BTC");
  const[chartType,setChartType]=useState("Area");
  const[timeframe,setTimeframe]=useState(30);
  const[history,setHistory]=useState({});
  const[activeTab,setActiveTab]=useState("overview");
  const[showTimeout,setShowTimeout]=useState(false);
  const[timeoutSecs,setTimeoutSecs]=useState(10);
  const[lastUpdate,setLastUpdate]=useState(null);
  const inactRef=useRef(null);const countRef=useRef(null);

  const logout=useCallback(()=>{clearToken();setLoggedIn(false);setShowTimeout(false);clearTimeout(inactRef.current);clearInterval(countRef.current);},[]);

  const resetTimer=useCallback(()=>{
    if(!loggedIn)return;
    setShowTimeout(false);clearTimeout(inactRef.current);clearInterval(countRef.current);
    inactRef.current=setTimeout(()=>{
      setTimeoutSecs(10);setShowTimeout(true);let s=10;
      countRef.current=setInterval(()=>{s--;setTimeoutSecs(s);if(s<=0){clearInterval(countRef.current);logout();}},1000);
    },INACTIVITY_TIMEOUT-10000);
  },[loggedIn,logout]);

  useEffect(()=>{
    if(!loggedIn)return;
    const evts=["mousemove","mousedown","keydown","scroll","touchstart","click"];
    evts.forEach(e=>window.addEventListener(e,resetTimer,{passive:true}));
    resetTimer();
    return()=>{evts.forEach(e=>window.removeEventListener(e,resetTimer));clearTimeout(inactRef.current);clearInterval(countRef.current);};
  },[loggedIn,resetTimer]);

  const fetchAll=useCallback(async()=>{
    if(!getToken())return;
    try{
      const[sRes,pRes,mRes,prRes]=await Promise.all([
        authFetch(`${API_BASE}/api/signals`),
        authFetch(`${API_BASE}/api/positions`),
        authFetch(`${API_BASE}/api/market`),
        authFetch(`${API_BASE}/api/projections`),
      ]);
      if(sRes.status===401||pRes.status===401){logout();return;}
      if(sRes.ok)setSignals(await sRes.json());
      if(pRes.ok)setPositions(await pRes.json());
      if(mRes.ok)setMarket(await mRes.json());
      if(prRes.ok)setProjections(await prRes.json());
      setLastUpdate(new Date());
    }catch(e){console.error(e);}
  },[logout]);

  const fetchHistory=useCallback(async(coin)=>{
    if(history[coin])return;
    // Try backend history endpoint, fall back to CoinGecko
    try{
      const r=await authFetch(`${API_BASE}/api/history/${coin}`);
      if(r.ok){const j=await r.json();setHistory(h=>({...h,[coin]:j}));return;}
    }catch{}
    // Fallback: CoinGecko public API
    try{
      const ids={BTC:"bitcoin",ETH:"ethereum",SOL:"solana",BNB:"binancecoin",XRP:"ripple",DOGE:"dogecoin",ADA:"cardano",AVAX:"avalanche-2",LINK:"chainlink",LTC:"litecoin",XLM:"stellar",SHIB:"shiba-inu",PEPE:"pepe"};
      const id=ids[coin]||coin.toLowerCase();
      const days=365;
      const r=await fetch(`https://api.coingecko.com/api/v3/coins/${id}/market_chart?vs_currency=usd&days=${days}`);
      if(r.ok){
        const j=await r.json();
        const data=j.prices.map(([ts,price])=>({date:new Date(ts).toISOString(),price}));
        setHistory(h=>({...h,[coin]:data}));
      }
    }catch{}
  },[history]);

  useEffect(()=>{if(!loggedIn)return;fetchAll();const iv=setInterval(fetchAll,90000);return()=>clearInterval(iv);},[loggedIn,fetchAll]);
  useEffect(()=>{if(loggedIn&&selectedCoin)fetchHistory(selectedCoin);},[loggedIn,selectedCoin,fetchHistory]);

  if(!loggedIn)return<LoginScreen onLogin={()=>{setLoggedIn(true);}}/>;

  const fg=market.fear_greed||market.fg||{};
  const fgVal=fg.value||market.fg_value||0;
  const fgColor=fgVal>=75?"#22c55e":fgVal>=55?"#f59e0b":fgVal>=30?"#f97316":"#ef4444";
  const totalPnl=positions.reduce((s,p)=>s+(p.pnl_usd||p.unrealized_pnl||0),0);
  const openCount=positions.filter(p=>p.status==="OPEN"||p.status==="open").length;

  // Build coin data from market + signals
  const coinData={};
  if(market.prices)Object.entries(market.prices).forEach(([sym,d])=>{coinData[sym]=d;});
  if(market.coin_data)Object.entries(market.coin_data).forEach(([sym,d])=>{coinData[sym]={...coinData[sym],...d};});

  const card={background:"#1a1b2e",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"14px",padding:"18px"};
  const tabBtn=t=>({background:activeTab===t?"rgba(99,102,241,0.2)":"transparent",border:activeTab===t?"1px solid rgba(99,102,241,0.4)":"1px solid transparent",color:activeTab===t?"#a5b4fc":"rgba(255,255,255,0.4)",padding:"7px 16px",borderRadius:"8px",fontSize:"12px",fontWeight:"500",cursor:"pointer"});

  const CoinRow=({sym})=>{
    const cd=coinData[sym]||{};
    const pos=positions.find(p=>(p.symbol||p.coin)===sym);
    const sig=signals.find(s=>(s.symbol||s.coin)===sym);
    const pct=cd.change_24h||cd.price_change_24h;
    const isUp=(pct||0)>=0;
    const isActive=selectedCoin===sym;
    return(
      <div onClick={()=>{setSelectedCoin(sym);setActiveTab("chart");}}
        style={{display:"flex",alignItems:"center",padding:"9px 12px",borderRadius:"9px",cursor:"pointer",
          background:isActive?"rgba(99,102,241,0.12)":"transparent",
          border:isActive?"1px solid rgba(99,102,241,0.3)":"1px solid transparent",
          transition:"all 0.12s",marginBottom:"2px"}}>
        <div style={{width:"38px",fontSize:"12px",fontWeight:"700",color:"rgba(255,255,255,0.9)",fontFamily:"monospace"}}>{sym}</div>
        <div style={{flex:1,fontSize:"11px",color:"rgba(255,255,255,0.55)"}}>{cd.price?`$${Number(cd.price).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:cd.price<1?6:2})}`:""}</div>
        <div style={{width:"52px",fontSize:"11px",fontWeight:"600",color:isUp?"#22c55e":"#ef4444",textAlign:"right"}}>{pct!=null?`${isUp?"+":""}${Number(pct).toFixed(1)}%`:""}</div>
        <div style={{width:"60px",display:"flex",justifyContent:"flex-end",marginLeft:"5px"}}>
          <Sparkline data={cd.sparkline||cd.sparkline_7d} color={isUp?"#22c55e":"#ef4444"} width={52} height={22}/>
        </div>
        {pos&&<div style={{width:"5px",height:"5px",borderRadius:"50%",background:"#6366f1",marginLeft:"5px",flexShrink:0}}/>}
        {(sig?.direction==="BUY"||(sig?.analysis?.direction==="BUY"))&&<div style={{width:"5px",height:"5px",borderRadius:"50%",background:"#22c55e",marginLeft:"3px",flexShrink:0}}/>}
      </div>
    );
  };

  return(
    <div style={{minHeight:"100vh",background:"#0d0e1a",color:"#fff",fontFamily:"system-ui,sans-serif"}}>
      {showTimeout&&<TimeoutModal secondsLeft={timeoutSecs} onStay={resetTimer}/>}

      {/* Header */}
      <div style={{borderBottom:"1px solid rgba(255,255,255,0.06)",padding:"12px 22px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
          <span style={{fontSize:"18px"}}>📈</span>
          <span style={{fontSize:"15px",fontWeight:"800",letterSpacing:"-0.5px"}}>DigitalBalm</span>
          <span style={{fontSize:"10px",color:"rgba(255,255,255,0.3)",background:"rgba(255,255,255,0.05)",padding:"2px 8px",borderRadius:"20px"}}>V10 LIVE</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"14px"}}>
          <div style={{fontSize:"11px",color:"rgba(255,255,255,0.3)"}}>{lastUpdate?`Updated ${lastUpdate.toLocaleTimeString()}`:"Loading..."}</div>
          <div style={{display:"flex",alignItems:"center",gap:"5px"}}>
            <div style={{width:"6px",height:"6px",borderRadius:"50%",background:"#22c55e",boxShadow:"0 0 5px #22c55e"}}/>
            <span style={{fontSize:"11px",color:"rgba(255,255,255,0.4)"}}>Live</span>
          </div>
          <button onClick={logout} style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.2)",color:"#f87171",padding:"5px 12px",borderRadius:"7px",fontSize:"11px",cursor:"pointer"}}>Sign Out</button>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"262px 1fr",height:"calc(100vh - 53px)"}}>

        {/* Sidebar */}
        <div style={{borderRight:"1px solid rgba(255,255,255,0.06)",overflowY:"auto",padding:"12px 10px"}}>
          {/* F&G */}
          <div style={{...card,marginBottom:"10px",padding:"12px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:"6px"}}>
              <span style={{fontSize:"10px",color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"0.5px"}}>Fear & Greed</span>
              <span style={{fontSize:"10px",color:fgColor,fontWeight:"600"}}>{fg.classification||market.fg_class||"—"}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
              <div style={{fontSize:"26px",fontWeight:"800",color:fgColor}}>{fgVal}</div>
              <div style={{flex:1,height:"5px",background:"rgba(255,255,255,0.06)",borderRadius:"3px",overflow:"hidden"}}>
                <div style={{width:`${fgVal}%`,height:"100%",background:"linear-gradient(90deg,#ef4444,#f59e0b,#22c55e)",borderRadius:"3px"}}/>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px",marginBottom:"10px"}}>
            {[{label:"Open",value:openCount,color:"#6366f1"},{label:"P&L",value:fmtUsd(totalPnl),color:totalPnl>=0?"#22c55e":"#ef4444"}].map(s=>(
              <div key={s.label} style={{...card,padding:"10px",textAlign:"center"}}>
                <div style={{fontSize:"10px",color:"rgba(255,255,255,0.4)",marginBottom:"3px"}}>{s.label}</div>
                <div style={{fontSize:"15px",fontWeight:"700",color:s.color}}>{s.value}</div>
              </div>
            ))}
          </div>

          <div style={{fontSize:"10px",color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"6px",padding:"0 4px"}}>All Assets</div>
          {ALL_COINS.map(sym=><CoinRow key={sym} sym={sym}/>)}
        </div>

        {/* Main */}
        <div style={{overflowY:"auto",padding:"18px 20px"}}>
          <div style={{display:"flex",gap:"6px",marginBottom:"16px"}}>
            {["overview","chart","positions","signals","projections"].map(t=>(
              <button key={t} onClick={()=>setActiveTab(t)} style={tabBtn(t)}>
                {t.charAt(0).toUpperCase()+t.slice(1)}
              </button>
            ))}
          </div>

          {/* Overview */}
          {activeTab==="overview"&&(
            <div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"10px",marginBottom:"14px"}}>
                {[
                  {label:"BTC Dominance",value:`${market.btc_dominance?.toFixed?.(1)||"—"}%`,icon:"₿"},
                  {label:"Market Phase",value:market.monthly_regime||market.regime||"—",icon:"🌊"},
                  {label:"Macro Mode",value:market.macro_mode||"—",icon:"⚡"},
                  {label:"MVRV Zone",value:market.mvrv_zone||"—",icon:"📊"},
                ].map(m=>(<div key={m.label} style={card}><div style={{fontSize:"18px",marginBottom:"5px"}}>{m.icon}</div><div style={{fontSize:"10px",color:"rgba(255,255,255,0.4)",marginBottom:"3px"}}>{m.label}</div><div style={{fontSize:"14px",fontWeight:"700"}}>{m.value}</div></div>))}
              </div>

              <div style={{...card,marginBottom:"12px"}}>
                <div style={{fontSize:"12px",fontWeight:"600",color:"rgba(255,255,255,0.7)",marginBottom:"12px"}}>Open Positions</div>
                {positions.filter(p=>p.status==="OPEN"||p.status==="open").length===0
                  ?<div style={{color:"rgba(255,255,255,0.3)",fontSize:"13px",textAlign:"center",padding:"20px 0"}}>No open positions</div>
                  :positions.filter(p=>p.status==="OPEN"||p.status==="open").map((pos,i)=>{
                    const pct=pos.pnl_pct||pos.unrealized_pct||0;
                    return(<div key={i} style={{display:"flex",alignItems:"center",padding:"9px 12px",background:"rgba(255,255,255,0.03)",borderRadius:"8px",border:"1px solid rgba(255,255,255,0.05)",marginBottom:"5px"}}>
                      <div style={{width:"44px",fontWeight:"700",fontSize:"12px"}}>{pos.symbol||pos.coin}</div>
                      <div style={{flex:1,fontSize:"11px",color:"rgba(255,255,255,0.5)"}}>{pos.profile?.toUpperCase?.()||""} · ${(pos.position_usd||pos.size||0).toFixed?.(0)}</div>
                      <div style={{fontSize:"12px",fontWeight:"600",color:pct>=0?"#22c55e":"#ef4444"}}>{fmtPct(pct)}</div>
                    </div>);
                  })
                }
              </div>

              <div style={card}>
                <div style={{fontSize:"12px",fontWeight:"600",color:"rgba(255,255,255,0.7)",marginBottom:"12px"}}>Recent Signals</div>
                {signals.length===0
                  ?<div style={{color:"rgba(255,255,255,0.3)",fontSize:"13px",textAlign:"center",padding:"20px 0"}}>No signals this scan</div>
                  :signals.slice(0,8).map((sig,i)=>{
                    const dir=sig.direction||sig.analysis?.direction;
                    const conf=sig.confidence||sig.analysis?.confidence;
                    const color=dir==="BUY"?"#22c55e":dir==="SELL"?"#ef4444":"#94a3b8";
                    return(<div key={i} style={{display:"flex",alignItems:"center",padding:"8px 12px",background:"rgba(255,255,255,0.03)",borderRadius:"8px",border:"1px solid rgba(255,255,255,0.05)",marginBottom:"5px"}}>
                      <div style={{width:"6px",height:"6px",borderRadius:"50%",background:color,marginRight:"10px",flexShrink:0}}/>
                      <div style={{width:"40px",fontWeight:"700",fontSize:"12px"}}>{sig.symbol||sig.coin}</div>
                      <div style={{flex:1,fontSize:"11px",color:"rgba(255,255,255,0.5)"}}>Conf: {conf||"—"}%</div>
                      <div style={{fontSize:"12px",fontWeight:"600",color}}>{dir||"HOLD"}</div>
                    </div>);
                  })
                }
              </div>
            </div>
          )}

          {/* Chart */}
          {activeTab==="chart"&&(
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"14px"}}>
                <div>
                  <span style={{fontSize:"20px",fontWeight:"800"}}>{selectedCoin}</span>
                  {coinData[selectedCoin]?.price&&<span style={{fontSize:"16px",color:"rgba(255,255,255,0.6)",marginLeft:"10px"}}>${Number(coinData[selectedCoin].price).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:coinData[selectedCoin].price<1?6:2})}</span>}
                  {coinData[selectedCoin]?.change_24h!=null&&<span style={{fontSize:"12px",color:coinData[selectedCoin].change_24h>=0?"#22c55e":"#ef4444",marginLeft:"8px",fontWeight:"600"}}>{fmtPct(coinData[selectedCoin].change_24h)} 24h</span>}
                </div>
                <div style={{display:"flex",gap:"6px"}}>
                  <div style={{display:"flex",background:"rgba(255,255,255,0.05)",borderRadius:"7px",padding:"2px"}}>
                    {CHART_TYPES.map(ct=>(<button key={ct} onClick={()=>setChartType(ct)} style={{background:chartType===ct?"rgba(99,102,241,0.3)":"transparent",border:"none",color:chartType===ct?"#a5b4fc":"rgba(255,255,255,0.4)",padding:"4px 10px",borderRadius:"5px",fontSize:"11px",cursor:"pointer",fontWeight:"500"}}>{ct}</button>))}
                  </div>
                  <div style={{display:"flex",background:"rgba(255,255,255,0.05)",borderRadius:"7px",padding:"2px"}}>
                    {TIMEFRAMES.map(tf=>(<button key={tf.label} onClick={()=>setTimeframe(tf.days)} style={{background:timeframe===tf.days?"rgba(99,102,241,0.3)":"transparent",border:"none",color:timeframe===tf.days?"#a5b4fc":"rgba(255,255,255,0.4)",padding:"4px 9px",borderRadius:"5px",fontSize:"11px",cursor:"pointer",fontWeight:"500"}}>{tf.label}</button>))}
                  </div>
                </div>
              </div>
              <div style={{...card,height:"290px",marginBottom:"14px"}}>
                {history[selectedCoin]
                  ?<InteractiveChart history={history[selectedCoin]} symbol={selectedCoin} chartType={chartType} timeframeDays={timeframe}/>
                  :<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",color:"rgba(255,255,255,0.3)",fontSize:"13px"}}>Loading chart...</div>
                }
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"10px"}}>
                {[{label:"24h High",value:fmtUsd(coinData[selectedCoin]?.high_24h)},{label:"24h Low",value:fmtUsd(coinData[selectedCoin]?.low_24h)},{label:"Volume",value:coinData[selectedCoin]?.volume_24h?`$${(coinData[selectedCoin].volume_24h/1e9).toFixed(1)}B`:"—"},{label:"Market Cap",value:coinData[selectedCoin]?.market_cap?`$${(coinData[selectedCoin].market_cap/1e9).toFixed(1)}B`:"—"}].map(s=>(
                  <div key={s.label} style={card}><div style={{fontSize:"10px",color:"rgba(255,255,255,0.4)",marginBottom:"3px"}}>{s.label}</div><div style={{fontSize:"14px",fontWeight:"600"}}>{s.value}</div></div>
                ))}
              </div>
            </div>
          )}

          {/* Positions */}
          {activeTab==="positions"&&(
            <div style={card}>
              <div style={{fontSize:"12px",fontWeight:"600",color:"rgba(255,255,255,0.7)",marginBottom:"14px"}}>All Positions</div>
              {positions.length===0
                ?<div style={{color:"rgba(255,255,255,0.3)",fontSize:"13px",textAlign:"center",padding:"40px 0"}}>No positions on record</div>
                :<table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
                  <thead><tr style={{color:"rgba(255,255,255,0.4)",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                    {["Symbol","Profile","Entry","P&L %","P&L $","Stop","TP","Status"].map(h=>(<th key={h} style={{padding:"7px 10px",textAlign:"left",fontSize:"10px",fontWeight:"500"}}>{h}</th>))}
                  </tr></thead>
                  <tbody>{positions.map((pos,i)=>{
                    const pct=pos.pnl_pct||pos.unrealized_pct||0;
                    const col=pct>=0?"#22c55e":"#ef4444";
                    return(<tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                      <td style={{padding:"9px 10px",fontWeight:"700"}}>{pos.symbol||pos.coin}</td>
                      <td style={{padding:"9px 10px",color:"rgba(255,255,255,0.5)",fontSize:"10px",textTransform:"uppercase"}}>{pos.profile||"—"}</td>
                      <td style={{padding:"9px 10px"}}>{fmtUsd(pos.entry_price||pos.entry)}</td>
                      <td style={{padding:"9px 10px",color:col,fontWeight:"600"}}>{fmtPct(pct)}</td>
                      <td style={{padding:"9px 10px",color:col,fontWeight:"600"}}>{fmtUsd(pos.pnl_usd||pos.unrealized_pnl)}</td>
                      <td style={{padding:"9px 10px",color:"#ef4444",fontSize:"11px"}}>{fmtUsd(pos.stop_loss)}</td>
                      <td style={{padding:"9px 10px",color:"#22c55e",fontSize:"11px"}}>{fmtUsd(pos.take_profit)}</td>
                      <td style={{padding:"9px 10px"}}>
                        <span style={{background:pos.status==="OPEN"||pos.status==="open"?"rgba(99,102,241,0.15)":"rgba(255,255,255,0.05)",color:pos.status==="OPEN"||pos.status==="open"?"#a5b4fc":"rgba(255,255,255,0.4)",padding:"2px 7px",borderRadius:"4px",fontSize:"10px"}}>{pos.status}</span>
                      </td>
                    </tr>);
                  })}</tbody>
                </table>
              }
            </div>
          )}

          {/* Signals */}
          {activeTab==="signals"&&(
            <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
              {signals.length===0
                ?<div style={{...card,color:"rgba(255,255,255,0.3)",textAlign:"center",padding:"40px"}}>No signals this scan</div>
                :signals.map((sig,i)=>{
                  const dir=sig.direction||sig.analysis?.direction;
                  const conf=sig.confidence||sig.analysis?.confidence;
                  const color=dir==="BUY"?"#22c55e":dir==="SELL"?"#ef4444":"#94a3b8";
                  return(<div key={i} style={{...card,borderLeft:`3px solid ${color}`}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"8px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                        <span style={{fontWeight:"800",fontSize:"15px"}}>{sig.symbol||sig.coin}</span>
                        <span style={{background:`${color}22`,color,padding:"2px 9px",borderRadius:"5px",fontSize:"11px",fontWeight:"600"}}>{dir||"HOLD"}</span>
                      </div>
                      <div style={{fontSize:"11px",color:"rgba(255,255,255,0.5)"}}>Confidence: <span style={{color:"#fff",fontWeight:"600"}}>{conf||"—"}%</span></div>
                    </div>
                    {(sig.reasoning||sig.analysis?.reasoning)&&<div style={{fontSize:"12px",color:"rgba(255,255,255,0.55)",lineHeight:"1.5",background:"rgba(255,255,255,0.03)",padding:"9px",borderRadius:"7px"}}>{sig.reasoning||sig.analysis?.reasoning}</div>}
                    {sig.profiles?.length>0&&<div style={{display:"flex",gap:"5px",marginTop:"8px"}}>{sig.profiles.map(p=>(<span key={p} style={{background:"rgba(99,102,241,0.15)",color:"#a5b4fc",padding:"2px 8px",borderRadius:"5px",fontSize:"10px"}}>{p.toUpperCase()}</span>))}</div>}
                  </div>);
                })
              }
            </div>
          )}

          {/* Projections */}
          {activeTab==="projections"&&(
            <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
              {projections.length===0
                ?<div style={{...card,color:"rgba(255,255,255,0.3)",textAlign:"center",padding:"40px"}}>No projections available</div>
                :projections.map((proj,i)=>(
                  <div key={i} style={card}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"8px"}}>
                      <span style={{fontWeight:"800",fontSize:"15px"}}>{proj.symbol||proj.coin}</span>
                      <span style={{fontSize:"11px",color:"rgba(255,255,255,0.4)"}}>{proj.timeframe||"7d"}</span>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"8px"}}>
                      {[{label:"Target",value:fmtUsd(proj.target||proj.price_target),color:"#22c55e"},{label:"Stop",value:fmtUsd(proj.stop||proj.stop_loss),color:"#ef4444"},{label:"Confidence",value:`${proj.confidence||"—"}%`,color:"#a5b4fc"}].map(s=>(
                        <div key={s.label} style={{background:"rgba(255,255,255,0.03)",borderRadius:"8px",padding:"8px"}}>
                          <div style={{fontSize:"10px",color:"rgba(255,255,255,0.4)",marginBottom:"3px"}}>{s.label}</div>
                          <div style={{fontSize:"13px",fontWeight:"600",color:s.color}}>{s.value}</div>
                        </div>
                      ))}
                    </div>
                    {proj.reasoning&&<div style={{fontSize:"12px",color:"rgba(255,255,255,0.5)",lineHeight:"1.5",marginTop:"10px",padding:"9px",background:"rgba(255,255,255,0.03)",borderRadius:"7px"}}>{proj.reasoning}</div>}
                  </div>
                ))
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
